<template>
    <page
        :id="state_identifier"
        title="Contacts"
        :status="page_status"
        :waiting="waiting"
        @statused="statused"
    >
        <template v-slot:title><span></span></template>

        <div class="uk-margin-top">
            <data-table
                id="contacts"
                :columns="visible_columns"
                :rows="filtered_rows"
                :search="state.search"
                :total="state.total"
                :found="state.found"
                :current-page="state.page"
                :total-pages="nb_pages"
                :searchable="false"
                table-class="lam-records-list lam-contacts-list"
                empty-title="No contacts found"
                @sort="sort_by"
                @search="search"
                @page="goto_page"
                @click="record_edit(entity, $event.id)"
            >
                <template v-slot:controls>
                    <div class="uk-grid uk-grid-small uk-width-1-1">
                        <div class="uk-width-1-1 uk-width-small-2-3">
                            <base-button
                                flavor="link"
                                @click.prevent="toggle_filters(true)"
                            >
                                <span v-html="applied_filters"></span>
                            </base-button>
                            <base-button
                                v-if="!!filter_description"
                                flavor="link"
                                @click.prevent="clear_filters"
                                class="uk-margin-left"
                            >
                                <open-icon glyph="close" />
                            </base-button>
                        </div>
                        <div class="uk-width-1-1 uk-width-small-1-3 uk-text-right uk-text-left-small">
                            <form-checkbox
                                id="user_id"
                                :value="!!state.user_id"
                                name="user_id"
                                @input="toggle_user_id"
                            >
                                My Clients
                            </form-checkbox>
                        </div>
                    </div>
                </template>
                <template
                    v-slot:summary="{ row }"
                >
                    <div class="uk-panel uk-panel-box">
                        <div class="uk-panel-teaser">
                            <contact-avatar
                                :contact="row"
                                :editable="false"
                                :required="false"
                            />
                        </div>
                        <div class="uk-panel-body">
                            <h3 v-if="row.name" class="uk-panel-title">
                                {{ row.name }}
                            </h3>
                            <h5 v-if="row.firm_name">
                                {{ row.firm_name }}
                            </h5>
                            <div v-if="row.city_state">
                                {{ row.city_state }}
                            </div>
                            <div v-if="row.title" >
                                {{ row.title }}
                            </div>
                        </div>
                    </div>
                </template>
                <template
                    v-slot:tbody="{ row, field }"
                >
                    <router-link
                        v-if="field === 'name'"
                        :to="{ name: 'contact.edit', params: { id: row.id } }"
                    >
                        {{ row.name }}
                    </router-link>
                    <span v-else-if="field.match(/^nb_(prospective|active|completed)_projects/)">{{ row.project_statistics[field] | nibnut.number("0,0") }}</span>
                    <a v-else-if="field === 'main_email'" :href="row.main_email | nibnut.mailto" @click.stop>{{ row.main_email }}</a>
                    <a v-else-if="field === 'main_phone'" :href="(row.main_mobile || row.main_phone) | nibnut.telto" @click.stop>{{ row.main_mobile || row.main_phone }}</a>
                    <span v-else-if="field === 'position_filters'">{{ row.position_filters.join(", ") }}</span>
                    <div v-else-if="field === 'comments'">
                        <button v-if="row.comment_ids.length" type="button" class="uk-button uk-button-link uk-button-small" @click.prevent.stop="display_comments_for_contact(row)">
                            <open-icon glyph="sticky-note" />
                        </button>
                    </div>
                    <span v-else-if="field === 'created_at_as_string'">{{ row.created_at_as_string | nibnut.date }}</span>
                    <span v-else>{{ row[field] }}</span>
                </template>
            </data-table>
        </div>
        <modal-dialog
            id="contacts-filters"
            :show.sync="showing_filters"
        >
            <template v-slot:title>
                <h3>Filter List of Contacts</h3>
            </template>

            <form class="uk-form uk-margin-top">
                <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                    <div class="uk-width-1-1">
                        <form-tags-input
                            id="filter-tags"
                            name="tag_ids"
                            :value="state.tag_ids"
                            :local-options="tags"
                            :required="false"
                            :editable="true"
                            :can-add="false"
                            class="uk-width-1-1"
                            @input="filter_by_tags"
                        >
                            <template v-slot:label>Tag(s):</template>
                        </form-tags-input>
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                    <div class="uk-width-1-1">
                        <form-input
                            id="filter-city_state"
                            name="city_state"
                            :value="state.filter"
                            :required="false"
                            :editable="true"
                            class="uk-width-1-1"
                            @input="filter('city_state', $event)"
                        >
                            <template v-slot:label>City:</template>
                        </form-input>
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                    <div class="uk-width-1-1">
                        <form-input
                            id="filter-created_at"
                            name="created_at"
                            :value="state.created_at"
                            :required="false"
                            :editable="true"
                            class="uk-width-1-1"
                            @input="filter_by_created_at"
                        >
                            <template v-slot:label>Added to Marge:</template>
                            <template v-slot:hint>
                                (Ex: "March 15, 2023", "2023-03-15 or 2023-03-16" or "2023-03-15 to 2023-03-20")
                            </template>
                        </form-input>
                    </div>
                </div>
            </form>
        </modal-dialog>
        <comments-dialog
            :show.sync="displaying_comments"
            :title="`Comments for ${displayed_contact.name}`"
            :comments="displayed_comments"
            :owner-id="displayed_contact.id || 0"
            owner-type="App.Contact"
            :editable="false"
        />
    </page>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_records_list_page from "../../mixins/IsRecordsListPage"
import string_utilities from "../../mixins/StringUtilities"

import FormCheckbox from "../../ui/FormCheckbox"
import FormInput from "../../ui/FormInput"
import FormTagsInput from "../../ui/FormTagsInput"
import ContactAvatar from "../../ui/ContactAvatar"
import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import CommentsDialog from "../../dialogs/CommentsDialog"
import BaseButton from "../../ui/BaseButton"
import OpenIcon from "../../ui/OpenIcon"

export default {
    mixins: [is_records_list_page, string_utilities],
    components: {
        FormCheckbox,
        FormInput,
        FormTagsInput,
        ContactAvatar,
        ModalDialog,
        CommentsDialog,
        BaseButton,
        OpenIcon
    },
    methods: {
        pre_load () {
            if(this.state.user_id === null) {
                this.set_state_values({
                    user_id: this.profile_id
                })
            }
        },
        filter_rows (rows) {
            return rows
        },
        toggle_user_id () {
            this.set_state_values({
                page: 1,
                user_id: this.state.user_id ? 0 : this.profile_id
            })
            this.refresh()
        },
        toggle_filters (show) {
            this.showing_filters = show
        },
        filter_by_tags (tag_ids) {
            this.set_state_values({
                page: 1,
                tag_ids
            })
            this.refresh()
        },
        filter_by_created_at (created_at) {
            // March 15, 2023
            // 2023-03-15 or 2023-03-16 | 2023-03-15|2023-03-16
            // 2023-03-15 to 2023-03-20 | 2023-03-15:2023-03-20
            this.set_state_values({
                page: 1,
                created_at
            })
            this.refresh()
        },
        clear_filters () {
            const new_state = {
                filter: null,
                tag_ids: [],
                created_at: null
            }
            this.set_state_values(new_state)
            this.refresh()
        },
        display_comments_for_contact (contact) {
            this.displayed_contact = contact
            this.$store.dispatch(
                "FETCH_RECORDS",
                {
                    entity: "comment",
                    query: {
                        contact_id: this.displayed_contact.id
                    }
                }
            ).then(() => {
                this.displaying_comments = true
            }).catch(error => {
                this.$error(error.message)
            })
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        fields () {
            const fields = Object.keys(this.columns)
            fields.push("main_mobile")
            fields.push("project_statistics")
            fields.push("comment_ids")
            fields.push("remote_image_url")
            fields.push("image_url")
            fields.push("first_name")
            fields.push("last_name")
            return fields
        },
        tags () {
            return this.dictionary("AS_contact_tags", "name")
        },
        applied_filters () {
            return this.filter_description || "Filter selection of contacts..."
        },
        filter_description () {
            const criteria = []

            if(this.state.filter_on && this.state.filter) {
                criteria.push(`<strong>City: </strong> ${this.state.filter}`)
            }
            if(this.state.tag_ids && !!this.state.tag_ids.length) {
                const tags = this.tags.filter(tag => this.state.tag_ids.indexOf(tag.id) >= 0).map(tag => tag.name)
                criteria.push(`<strong>Tag(s): </strong> ${tags.join(", ")}`)
            }

            if(this.state.created_at) criteria.push("<strong>Added: </strong> " + this.state.created_at)

            return criteria.join(" AND ")
        },
        displayed_comments () {
            if(!this.displayed_contact || !this.displayed_contact.comment_ids) return []
            return this.entity_records("comment", this.displayed_contact.comment_ids)
        }
    },
    data () {
        return {
            entity: "contact",
            columns: {
                name: { label: "Name", sort: null, type: "plain", cell_class: "lam-col-name" },
                title: { label: "Title", sort: null, type: "plain" },
                position_filters: { label: "Position", sort: false, type: "plain" },
                firm_name: { label: "Firm", sort: null, type: "plain" },
                city_state: { label: "City", sort: null, type: "plain" },
                nb_prospective_projects: { label: "# Prospective ", sort: false, type: "plain" },
                nb_active_projects: { label: "# Active", sort: false, type: "plain" },
                nb_completed_projects: { label: "# Completed", sort: false, type: "plain" },
                main_email: { label: "Email", sort: null, type: "plain" },
                main_phone: { label: "Phone", sort: null, type: "plain" },
                comments: { label: "Notes", sort: false, type: "plain" },
                created_at_as_string: { label: "Added", sort: null, type: "plain" }
            },
            default_state: {
                per_page: 30,
                page: 1,
                sort_by: "name",
                sort_dir: "asc",
                filter_on: "city_state",
                filter: null,
                archived: false,
                search: "",
                total: 0,
                found: 0,
                user_id: null,
                tag_ids: [],
                created_at: null
            },
            showing_filters: false,
            displaying_comments: false,
            displayed_contact: {}
        }
    }
}
</script>

<style lang="less">
@import "@/assets/less/variables";

table.uk-table.lam-contacts-list {
    @media (max-width: @breakpoint-medium-max) {
        tbody {
            display: flex;
            flex-wrap: wrap;

            & > tr {
                flex: 1 0 50%;
                & > td {
                    display: block;
                    border: 0;
                }

                .uk-panel-box {
                    display: flex;
                    padding: 0;

                    .uk-panel-teaser {
                        flex: 0 0 30%;
                        margin: 0;
                    }
                    .uk-panel-body {
                        flex: 1 0 1%;

                        h1, h2, h3, h4, h5, h6, div {
                            margin: 0;

                            & + & {
                                margin-bottom: 5px;
                            }
                        }
                    }
                }
            }
        }
    }
    @media (max-width: @breakpoint-mini-max) {
        tbody {
            & > tr {
                flex: 1 0 100%;
            }
        }
    }
}
</style>
